import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Header from './Header';
import NoItems from 'components/NoItems';
import styles from './EmailsDashboard.module.scss';
import { NoResultsIcon } from 'static/images';
import { ReduxState } from 'types/redux';
import { openDeleteEmailPopup, sortEmails } from 'LoanOriginationSystemEmails/ActionCreator';
import {
  resetAllEmailsFilters,
  setSearchInputValue,
  toggleFiltersPopup,
} from 'LoanOriginationSystemEmails/Filters/ActionCreator';
import { LoanOriginationSystemEmailsFiltersState } from 'LoanOriginationSystemEmails/Filters/Types';
import { Email, EmailsSortingField } from 'api/LoanOriginationSystem/LoanOriginationSystemEmailsApi';
import { isEqual, omit } from 'lodash';
import { initialState as filtersInitialState } from 'LoanOriginationSystemEmails/Filters/Reducer';
import SearchNotFound from 'components/SearchNotFound';
import EmailsTable from './EmailsTable';
import { useHistory } from 'react-router-dom';
import { getOrganizationEmail } from 'utils/emailGetters';
import { NullableItems, PaginationProps } from 'pagination';

interface EmailsDashboardProps {
  organizationId: string;
  emails: NullableItems<Email>;
  paginationProps: PaginationProps;
}

const EmailsDashboard: FC<EmailsDashboardProps> = ({ organizationId, emails, paginationProps }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { filters, sortingType } = useSelector((state: ReduxState) => state.loanOriginationSystemEmails);

  const organizationEmail = getOrganizationEmail(organizationId);

  const handleSearch = (search: string) => {
    dispatch(setSearchInputValue(search, filters.selectedBorrower || undefined));
  };

  const handleSort = (field: EmailsSortingField, ascending: boolean) => {
    dispatch(sortEmails(field, ascending));
  };

  const handleFilterButtonClick = () => {
    dispatch(toggleFiltersPopup());
  };

  const handleResetAllFilters = () => {
    dispatch(resetAllEmailsFilters());
  };

  const handleOpenEmail = (id: string) => history.replace(`?emailId=${id}`);
  const handleDeleteEmail = (id: string) => dispatch(openDeleteEmailPopup(id));

  const areFiltersEqual = (fieldsToOmit: (keyof LoanOriginationSystemEmailsFiltersState)[] = []) =>
    isEqual(omit(filters, fieldsToOmit), omit(filtersInitialState, fieldsToOmit));

  const isNoEmails = paginationProps.itemsTotal === 0 && !filters.searchInputValue && areFiltersEqual();

  const renderBody = () => {
    if (filters.searchInputValue && paginationProps.itemsTotal === 0) {
      return <SearchNotFound searchValue={filters.searchInputValue} />;
    }

    if (paginationProps.itemsTotal === 0 && areFiltersEqual()) {
      return (
        <NoItems title="No emails have been added yet!" className={styles.noData} titleClassName={styles.noDataTitle} />
      );
    }

    if (paginationProps.itemsTotal === 0 && !areFiltersEqual()) {
      return (
        <NoItems
          title="No Results Found"
          icon={<NoResultsIcon />}
          buttonMessage="Clear filters"
          subtitle="Please remove or adjust your filters."
          onButtonClick={handleResetAllFilters}
          className={styles.noItemsContainer}
        />
      );
    }

    return (
      <EmailsTable
        paginationProps={paginationProps}
        emails={emails}
        onSort={handleSort}
        sortingType={sortingType}
        onOpenEmail={handleOpenEmail}
        onDeleteEmail={handleDeleteEmail}
        organizationEmail={organizationEmail}
        searchValue={filters.searchInputValue}
      />
    );
  };

  return (
    <>
      <Header
        searchInputValue={filters.searchInputValue}
        subTitleHidden={isNoEmails}
        filtersChanged={!areFiltersEqual(['searchInputValue'])}
        onSearch={handleSearch}
        openFiltersPopup={handleFilterButtonClick}
        organizationEmail={organizationEmail}
        areEmailsLoading={paginationProps.itemsTotal === undefined}
      />
      {renderBody()}
    </>
  );
};

export default EmailsDashboard;
